import React, { useEffect, useState } from 'react';
import { updateUserById } from '../../../../../../../../crud/users_api/users';
import { HiOutlinePencilSquare, HiOutlineTrash, HiOutlineClipboardDocument } from 'react-icons/hi2';
import { FaCog, FaWhatsapp } from 'react-icons/fa';
import { User } from '../../../../../types';
import { toast } from 'react-toastify';
import { getPermissionState } from '../../../../../../../../utils/functions';
import CreateUser from './components/CreateUser/CreateUser';
import UserInvitation from '../../../InvitacionesView/components/TableUserInvitations/components/UserInvitation/UserInvitation';
import EditUser from './components/EditUser/EditUser';
import DeleteUser from './components/DeleteUser/DeleteUser';
import { RoleOption,PuestoOption } from './types';
import './Table.scss';

interface TableProps {
  data: User[];
  setData: (data: any[]) => void;
  puestos: PuestoOption[];
  roles: RoleOption[];
  showSelection?: boolean;
  selectedUsers?: Set<number>;
  onUserSelectionChange?: (selected: Set<number>) => void;
  showCreateModal?: boolean;
  onCloseCreateModal?: () => void;
}

const Table: React.FC<TableProps> = ({ 
  data, 
  setData, 
  puestos, 
  roles,
  showSelection: externalShowSelection = false,
  selectedUsers: externalSelectedUsers = new Set(),
  onUserSelectionChange,
  showCreateModal: externalShowCreateModal,
  onCloseCreateModal,
 }) => {
  const [originalUsers, setOriginalUsers] = useState<User[]>([]);
  const [currentData, setCurrentData] = useState<User[]>(data);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [internalShowCreateModal, setInternalShowCreateModal] = useState(false);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);

  // Use external selection state or internal fallback
  const showSelection = externalShowSelection;
  const selectedUsers = externalSelectedUsers;
  
  // Use external create modal state or internal fallback
  const showCreateModal = externalShowCreateModal !== undefined ? externalShowCreateModal : internalShowCreateModal;
  const handleCloseCreateModal = onCloseCreateModal || (() => setInternalShowCreateModal(false));

  useEffect(() => {
    setHasPermission(getPermissionState(42));
  }, []);

  useEffect(() => {
    setCurrentData(data);
    // Inicializar originalUsers con la lista recibida para que EditUser use la referencia correcta
    setOriginalUsers(data);
  }, [data]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleUserSelection = (id: string) => {
    if (onUserSelectionChange) {
      const numId = parseInt(id);
      const newSelection = new Set(selectedUsers);
      if (newSelection.has(numId)) {
        newSelection.delete(numId);
      } else {
        newSelection.add(numId);
      }
      onUserSelectionChange(newSelection);
    }
  };

  const handleShowDeleteModal = (user: User) => {
    setCurrentUser(user);
    setShowDeleteModal(true);
  };

  const handleShowEditModal = (user: User) => {
    setCurrentUser(user);
    setShowEditModal(true);
  };

  const toggleItemState = (id: string) => {
    const userToUpdate = currentData.find(u => u.user_id === id);
    if (!userToUpdate) return;
    updateUserById(Number(id), { state: !userToUpdate.state })
      .then(response => {
        toast.success('Estado de usuario actualizado');
        const updatedUser: User = response.data;
        const updated = data.map(u =>
          u.user_id === id ? { ...u, state: updatedUser.state } : u
        );
        setData(updated);
        setOriginalUsers(updated);
      })
      .catch(() => toast.error('Error al actualizar estado'));
  };

  const handleCopyToClipboard = (phoneNumber: string) => {
    navigator.clipboard.writeText(phoneNumber).then(() => {
      toast.success('Número copiado al portapapeles');
    }).catch(() => {
      toast.error('Error al copiar número');
    });
  };

  // This function will be called by the parent component through UsuariosView
  // We'll implement this logic in UsuariosView instead

  const renderTable = () => (
    <div className="mp-table-card usuarios-table-card">
      <div className="usuarios-table-scroll">
        <table className="mp-table">
          <thead className="mp-table-head">
            <tr>
              {showSelection && <th style={{ width: 40 }} />}
              <th>Nombre</th>
              <th style={{ width: 80, textAlign: 'center' }}>Estado</th>
              <th>Puesto</th>
              <th className="no-responsive">Rol</th>
              <th className="no-responsive" style={{ width: 160, textAlign: 'center' }}>Número</th>
              <th style={{ width: isMobile ? 60 : 120, textAlign: 'center' }}>{isMobile ? <FaCog /> : 'Acciones'}</th>
            </tr>
          </thead>
          <tbody className="mp-table-body">
          {currentData.map(user => (
            <tr key={user.user_id}>
              {showSelection && (
                <td>
                  <div className="mp-radio-button">
                    <input
                      id={`checkbox-${user.user_id}`}
                      type="checkbox"
                      checked={selectedUsers.has(parseInt(user.user_id))}
                      onChange={() => toggleUserSelection(user.user_id)}
                    />
                    <label htmlFor={`checkbox-${user.user_id}`}/>
                  </div>
                </td>
              )}
              <td>{user.username} {user.last_name}</td>
              <td>
                <label className="switch">
                  <input type="checkbox" checked={user.state} onChange={() => toggleItemState(user.user_id)} disabled={!hasPermission} />
                  <span className="slider round" />
                </label>
              </td>
              <td>
                {(() => {
                  const foundPuesto = puestos.find(p => Number(p.puesto_id) === user.puesto_id);
                  return foundPuesto?.puesto_name ?? 'No asignada';
                })()}
              </td>
              <td className="no-responsive">{roles.find(r => Number(r.rol_id) === Number(user.rol_id))?.rol_name ?? 'No asignado'}</td>
              <td className="no-responsive">
                {user.phone_number ? (
                  <span className="flex items-center justify-center gap-2">
                    <span>{user.phone_number}</span>
                    <button className="mp-btn-icon" onClick={() => handleCopyToClipboard(user.phone_number!)} title="Copiar" style={{ opacity: 0.4 }}>
                      <HiOutlineClipboardDocument size={14} />
                    </button>
                  </span>
                ) : (
                  <span style={{ opacity: 0.4, fontSize: '0.75rem' }}>N/A</span>
                )}
              </td>
              <td>
                <div className="flex items-center justify-center gap-1.5">
                  <button className="mp-btn-icon" onClick={() => window.open(`https://wa.me/${user.phone_number}`, '_blank', 'noopener,noreferrer')} title="WhatsApp">
                    <FaWhatsapp size={16} />
                  </button>
                  <button className="mp-btn-icon" onClick={() => handleShowEditModal(user)} disabled={!hasPermission} title="Editar">
                    <HiOutlinePencilSquare size={16} />
                  </button>
                  <button className="mp-btn-icon mp-btn-icon-danger" onClick={() => handleShowDeleteModal(user)} disabled={!hasPermission} title="Eliminar">
                    <HiOutlineTrash size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
    </div>
  );

  const renderNoData = () => (
    <div className="mp-table-card">
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <p className="text-sm" style={{ opacity: 0.5 }}>No hay usuarios disponibles.</p>
        <button className="mp-btn-primary" onClick={() => setInternalShowCreateModal(true)} disabled={!hasPermission}>
          Crear usuario
        </button>
      </div>
    </div>
  );

  return (
    <div>
      {currentData.length > 0 ? renderTable() : renderNoData()}

      <DeleteUser
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        currentUser={currentUser}
        currentData={currentData}
        setCurrentData={setData}
        originalUsers={originalUsers}
        setOriginalUsers={setOriginalUsers}
      />
      <CreateUser showCreateModal={showCreateModal} setShowCreateModal={handleCloseCreateModal} currentData={currentData} setCurrentData={setData} originalUsers={originalUsers} setOriginalUsers={setOriginalUsers} puestos={puestos} roles={roles} />
      <EditUser
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        setCurrentData={setCurrentData}
        currentData={currentData}
        currentUser={currentUser}
        originalUsers={originalUsers}
        setOriginalUsers={setOriginalUsers}
      />
      <UserInvitation show={showInvitationModal} handleClose={() => setShowInvitationModal(false)} onCreate={newInvitation => { setCurrentData(cd => [newInvitation, ...cd]); toast.success('Invitación creada exitosamente'); }} />
    </div>
  );
};

export default Table;
